import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '../../components/Navigation';
import { useNavigate } from 'react-router-dom';
import { offerService } from '../../services/offerService';
import FilterPanel from './components/FilterPanel';
import OfferCard from './components/OfferCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function OglasnaTablaBoard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    wishType: '',
    currency: '',
    minPrice: '',
    maxPrice: '',
  });
  const [sortBy, setSortBy] = useState('newest');
  const [activeNegotiations, setActiveNegotiations] = useState({});

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Auto-mark expired offers and archive completed-negotiation offers before fetching
      await offerService?.markExpiredOffers();
      await offerService?.archiveCompletedNegotiationOffers();

      const activeFilters = {
        ...filters,
        search: search?.trim(),
      };
      const data = await offerService?.getAllOffers(activeFilters);
      setOffers(data);

      // Fetch active negotiations for all displayed offers
      const offerIds = data?.map(o => o?.id)?.filter(Boolean);
      if (offerIds?.length > 0) {
        const negMap = await offerService?.getActiveNegotiationsForOffers(offerIds);
        setActiveNegotiations(negMap);
      } else {
        setActiveNegotiations({});
      }
    } catch (err) {
      setError(err?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [filters, search]);

  useEffect(() => {
    const timer = setTimeout(fetchOffers, 300);
    return () => clearTimeout(timer);
  }, [fetchOffers]);

  const sortedOffers = [...offers]?.sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.createdAt) - new Date(a.createdAt);
      case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
      case 'price_asc': return (a?.maxPrice || 0) - (b?.maxPrice || 0);
      case 'price_desc': return (b?.maxPrice || 0) - (a?.maxPrice || 0);
      case 'qty_desc': return (b?.tokenQuantity || 0) - (a?.tokenQuantity || 0);
      default: return 0;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('board.title')}</h1>
            <p className="text-gray-500 mt-1 text-sm">{t('board.subtitle')}</p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={fetchOffers}
              className="flex items-center gap-2 px-4 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
            >
              <RefreshCw size={16} />
              {t('board.refresh')}
            </button>
            <button
              onClick={() => navigate('/objavi-ponudu-form')}
              className="flex items-center gap-2 px-4 min-h-[44px] bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              {t('board.postOffer')}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative mb-6">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
            placeholder={t('board.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-3 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-base"
          />
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filter panel */}
          <div className="lg:w-64 flex-shrink-0">
            <FilterPanel
              filters={filters}
              onFiltersChange={setFilters}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />
          </div>

          {/* Offers grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)]?.map((_, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <p className="text-red-600">{error}</p>
                <button onClick={fetchOffers} className="mt-3 text-sm text-red-700 underline">{t('board.retryBtn')}</button>
              </div>
            ) : sortedOffers?.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <div className="text-gray-400 text-5xl mb-4">📋</div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">{t('board.noOffers')}</h3>
                <p className="text-gray-500 text-sm mb-4">
                  {search || filters?.wishType || filters?.currency
                    ? t('board.noOffersFiltered')
                    : t('board.beFirst')}
                </p>
                <button
                  onClick={() => navigate('/objavi-ponudu-form')}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors text-sm"
                >
                  {t('board.postFirst')}
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-4">{sortedOffers?.length} oglas{sortedOffers?.length !== 1 ? 'a' : ''}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {sortedOffers?.map(offer => (
                    <OfferCard
                      key={offer?.id}
                      offer={offer}
                      hasActiveNegotiation={!!activeNegotiations?.[offer?.id]}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
